import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/lib/i18n';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import axios from 'axios';
import { Clock, ArrowRight, ArrowLeft } from 'lucide-react';
import { API } from '@/lib/api';



export default function ContentPage() {
  const { t, language } = useLanguage();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const params = filter !== 'all' ? `?content_type=${filter}` : '';
        const res = await axios.get(`${API}/content${params}`);
        setItems(res.data);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    fetchContent();
  }, [filter]);

  const getTitle = (item) => language === 'de' ? item.title_de : item.title_en;
  const getDesc = (item) => language === 'de' ? item.description_de : item.description_en;
  const getContent = (item) => language === 'de' ? item.content_de : item.content_en;
  const typeColors = { recipe: 'bg-terracotta/10 text-terracotta', article: 'bg-sage/10 text-sage', tip: 'bg-sand text-foreground/70' };

  return (
    <div className="space-y-6 pb-24 lg:pb-8" data-testid="content-page">
      <div>
        <button onClick={() => nav('/dashboard')} className="flex items-center gap-1 text-foreground/40 hover:text-foreground font-body text-xs mb-2 transition-colors" data-testid="content-back-btn">
          <ArrowLeft size={14} /> Dashboard
        </button>
        <h1 className="font-display text-3xl md:text-4xl font-medium tracking-tight">{t('content.title')}</h1>
      </div>

      <Tabs value={filter} onValueChange={setFilter} className="w-full" data-testid="content-filter-tabs">
        <TabsList className="bg-warmgray/50 rounded-full p-1">
          <TabsTrigger value="all" className="rounded-full font-body text-sm data-[state=active]:bg-white" data-testid="filter-all">{t('content.all')}</TabsTrigger>
          <TabsTrigger value="recipe" className="rounded-full font-body text-sm data-[state=active]:bg-white" data-testid="filter-recipes">{t('content.recipes')}</TabsTrigger>
          <TabsTrigger value="article" className="rounded-full font-body text-sm data-[state=active]:bg-white" data-testid="filter-articles">{t('content.articles')}</TabsTrigger>
          <TabsTrigger value="tip" className="rounded-full font-body text-sm data-[state=active]:bg-white" data-testid="filter-tips">{t('content.tips')}</TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => <Card key={i} className="h-64 bg-warmgray/30 rounded-2xl animate-pulse" />)}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="content-grid">
          {items.map(item => (
            <Card key={item.id} className="bg-white border border-border/60 rounded-2xl overflow-hidden shadow-[0_2px_12px_-4px_rgba(0,0,0,0.05)] hover:shadow-md transition-all hover:-translate-y-1 cursor-pointer group" onClick={() => setSelected(item)} data-testid={`content-item-${item.id}`}>
              <div className="h-48 overflow-hidden">
                <img src={item.image_url} alt={getTitle(item)} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Badge className={`${typeColors[item.type]} border-0 rounded-full text-xs font-body`}>{t(`content.${item.type === 'recipe' ? 'recipes' : item.type === 'article' ? 'articles' : 'tips'}`)}</Badge>
                  {item.prep_time && (
                    <span className="flex items-center gap-1 font-body text-xs text-foreground/40"><Clock size={12} />{item.prep_time} {t('content.prep_time')}</span>
                  )}
                </div>
                <h3 className="font-display text-lg font-medium mb-2 group-hover:text-terracotta transition-colors">{getTitle(item)}</h3>
                <p className="font-body text-sm text-foreground/50 line-clamp-2">{getDesc(item)}</p>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="max-w-2xl bg-white rounded-2xl max-h-[85vh] overflow-y-auto" data-testid="content-detail-dialog">
          {selected && (
            <>
              <img src={selected.image_url} alt={getTitle(selected)} className="w-full h-64 object-cover rounded-xl mb-4" />
              <DialogHeader>
                <div className="flex items-center gap-2 mb-2">
                  <Badge className={`${typeColors[selected.type]} border-0 rounded-full text-xs font-body`}>{selected.type}</Badge>
                  {selected.prep_time && <span className="font-body text-xs text-foreground/40"><Clock size={12} className="inline mr-1" />{selected.prep_time} {t('content.prep_time')}</span>}
                </div>
                <DialogTitle className="font-display text-2xl font-medium">{getTitle(selected)}</DialogTitle>
              </DialogHeader>
              <p className="font-body text-foreground/60 mt-2 mb-4">{getDesc(selected)}</p>
              <div className="font-body text-sm text-foreground/80 leading-relaxed whitespace-pre-line">{getContent(selected)}</div>
              {selected.tags?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-6">
                  {selected.tags.map(tag => (
                    <Badge key={tag} variant="outline" className="rounded-full text-xs font-body">{tag}</Badge>
                  ))}
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
